import React, { useState, useEffect } from 'react';
import { isAuthenticated } from '../../utils/api';
import { initFeatherIcons } from '../../utils/featherIcons';
import Sidebar from './Sidebar';
import Header from './Header';
import Breadcrumbs from './Breadcrumbs';
import '../../pages/dashboard/Dashboard.css';

const MOBILE_BREAKPOINT = 768;
const FREIGHT_FORM_DRAFT_PREFIX = "freight_admin_form_draft";

function getDraftKey(pathname) {
  return `${FREIGHT_FORM_DRAFT_PREFIX}:${pathname || "unknown"}`;
}

function readDraft(pathname) {
  try {
    const raw = localStorage.getItem(getDraftKey(pathname));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeDraft(pathname, patch) {
  try {
    const prev = readDraft(pathname);
    localStorage.setItem(getDraftKey(pathname), JSON.stringify({ ...prev, ...patch }));
  } catch {
    /* ignore */
  }
}

function fieldPersistKey(el) {
  if (!el) return "";
  const n = el.getAttribute("name");
  if (n) return `name:${n}`;
  const i = el.getAttribute("id");
  if (i) return `id:${i}`;
  return "";
}

const DashboardLayout = ({
  children,
  breadcrumbs = [],
  searchTerm = '',
  onSearchChange = () => { },
  className = ''
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );

  // Check authentication on component mount
  useEffect(() => {
    if (!isAuthenticated()) {
      window.location.href = '/login';
      return;
    }
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsMobileViewport(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Initialize Feather icons after component mounts and updates
  useEffect(() => {
    initFeatherIcons();
  });

  // Freight admin forms: persist typed input values per route and restore on reload.
  // Scope is intentionally narrow (`form.freight-admin-form`) to avoid side effects elsewhere.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const pathname = window.location.pathname;
    const isFreightRoute = pathname.startsWith("/freight");
    if (!isFreightRoute) return undefined;

    const restoreTimer = window.setTimeout(() => {
      const draft = readDraft(pathname);
      const fields = document.querySelectorAll("form.freight-admin-form input, form.freight-admin-form textarea, form.freight-admin-form select");
      fields.forEach((node) => {
        const el = /** @type {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} */ (node);
        const key = fieldPersistKey(el);
        if (!key || !Object.prototype.hasOwnProperty.call(draft, key)) return;
        if (el instanceof HTMLInputElement && (el.type === "file" || el.type === "password" || el.type === "hidden")) {
          return;
        }
        const saved = draft[key];
        if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
          el.checked = !!saved;
        } else if (saved != null) {
          el.value = String(saved);
        }
        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }, 0);

    const onFieldChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
        return;
      }
      const form = target.closest("form.freight-admin-form");
      if (!form) return;
      if (target instanceof HTMLInputElement && (target.type === "file" || target.type === "password" || target.type === "hidden")) {
        return;
      }
      const key = fieldPersistKey(target);
      if (!key) return;
      const value =
        target instanceof HTMLInputElement && (target.type === "checkbox" || target.type === "radio")
          ? target.checked
          : target.value;
      writeDraft(pathname, { [key]: value });
    };

    document.addEventListener("input", onFieldChange, true);
    document.addEventListener("change", onFieldChange, true);
    return () => {
      window.clearTimeout(restoreTimer);
      document.removeEventListener("input", onFieldChange, true);
      document.removeEventListener("change", onFieldChange, true);
    };
  }, []);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMobileMenuToggle = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const closeMobileSidebar = () => {
    if (isMobileViewport) setSidebarCollapsed(false);
  };

  const mobileDrawerOpen = isMobileViewport && sidebarCollapsed;

  return (
    <div className={`dashboard-container${mobileDrawerOpen ? ' drawer-open' : ''}`}>
      {mobileDrawerOpen && (
        <button
          type="button"
          className="sidebar-drawer-backdrop"
          aria-label="Close menu"
          onClick={closeMobileSidebar}
        />
      )}

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
        isMobileViewport={isMobileViewport}
        onRequestClose={closeMobileSidebar}
      />

      <div className="dashboard-main">
        <Header
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          onMobileMenuToggle={handleMobileMenuToggle}
        />

        <main className={`dashboard-content ${className}`}>
          <Breadcrumbs items={breadcrumbs} />
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;